import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["lyamlKey"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import { getQuoteProperty } from 'quotes-ui-lib/utils/quote';
import { getStatusConfig } from 'quotes-ui-lib/utils/status';
import { StatusTag } from 'foundations-components/transitional/components/StatusTag';
import { QUOTE_SETTINGS, QUOTE_TOTAL_DISPLAY_PREFERENCE_KEYS } from 'quotes-ui-lib/constants/quoteSettings';
import { useFetchQuoteSetting } from 'quotes-ui-lib/hooks/useFetchQuoteSetting';
import { Small } from 'foundations-components/transitional/components/Small';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { WarningIcon } from 'foundations-assets';
import { useQuoteProgressionStatusDisplayConfig } from 'growth-payments-core/hooks/quotes/progressionStatus/useQuoteProgressionStatusDisplayConfig';
import { isCPQQuote } from 'quotes-ui-lib/utils/templates';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CPQQuoteStatusTag = ({
  quote
}) => {
  const progressionStatus = getQuoteProperty(quote, 'hs_quote_progression_status');
  const _useQuoteProgressionS = useQuoteProgressionStatusDisplayConfig(progressionStatus),
    {
      lyamlKey: progressionStatusLyamlKey
    } = _useQuoteProgressionS,
    statusDotProps = _objectWithoutPropertiesLoose(_useQuoteProgressionS, _excluded);
  if (!progressionStatusLyamlKey) {
    return /*#__PURE__*/_jsx("span", {
      children: "--"
    });
  }
  return /*#__PURE__*/_jsx(StatusTag, Object.assign({}, statusDotProps, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: progressionStatusLyamlKey
    })
  }));
};
const QuoteHighlightDetails = ({
  quote,
  allowPayBeforeEsign
}) => {
  const statusConfig = getStatusConfig(quote, allowPayBeforeEsign);
  const quoteTotalDisplayPreferenceSetting = useFetchQuoteSetting({
    quoteSettingKey: QUOTE_SETTINGS.QUOTE_TOTAL_DISPLAY_PREFERENCE
  });
  const quoteAmount = getQuoteProperty(quote, quoteTotalDisplayPreferenceSetting.value);
  if (!statusConfig) return null;
  return /*#__PURE__*/_jsxs(View, {
    as: "div",
    marginBlock: "400",
    textAlign: "left",
    children: [/*#__PURE__*/_jsx(Text, {
      marginBlockEnd: "200",
      as: "h3",
      children: getQuoteProperty(quote, 'hs_title')
    }), /*#__PURE__*/_jsxs(View, {
      marginBlockEnd: "100",
      as: "div",
      children: [quoteTotalDisplayPreferenceSetting.loading && /*#__PURE__*/_jsx(LoadingSpinner, {
        size: "xs",
        margin: "0"
      }), !quoteTotalDisplayPreferenceSetting.loading && /*#__PURE__*/_jsxs(_Fragment, {
        children: [quoteTotalDisplayPreferenceSetting.value === QUOTE_TOTAL_DISPLAY_PREFERENCE_KEYS.HS_TCV ? /*#__PURE__*/_jsx(View, {
          as: "span",
          marginInlineEnd: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "quotesHighlightCard.propertyLabels.totalContractValue"
          })
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(View, {
            as: "span",
            marginInlineEnd: "100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "quotesHighlightCard.propertyLabels.quoteAmount"
            })
          }), quoteTotalDisplayPreferenceSetting.error && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(View, {
              as: "span",
              marginInlineEnd: "100",
              children: /*#__PURE__*/_jsx(WarningIcon, {
                color: "icon-alert-default"
              })
            }), /*#__PURE__*/_jsx(Small, {
              use: "error",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "quotesUiLib.quoteTotalDisplayPreference.loadQuoteSettingErrorTooltip"
              })
            })]
          })]
        }), quoteAmount ? /*#__PURE__*/_jsx(FormattedIntlCurrency, {
          value: Number(quoteAmount),
          currencyCode: getQuoteProperty(quote, 'hs_currency')
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("span", {
            children: "--"
          }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(View, {
            as: "span",
            marginInlineEnd: "100",
            children: /*#__PURE__*/_jsx(WarningIcon, {
              color: "icon-alert-default"
            })
          }), /*#__PURE__*/_jsx(Small, {
            use: "error",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "quotesUiLib.quoteTotalDisplayPreference.quoteAmountErrorTooltip"
            })
          })]
        })]
      })]
    }), /*#__PURE__*/_jsxs(View, {
      marginBlockEnd: "100",
      as: "div",
      children: [/*#__PURE__*/_jsx(View, {
        as: "span",
        marginInlineEnd: "100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quotesHighlightCard.propertyLabels.number"
        })
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quotesHighlightCard.propertyValues.number",
        options: {
          number: getQuoteProperty(quote, 'hs_quote_number')
        }
      })]
    }), /*#__PURE__*/_jsxs(View, {
      marginBlockEnd: "100",
      as: "div",
      children: [/*#__PURE__*/_jsx(View, {
        as: "span",
        marginInlineEnd: "100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quotesHighlightCard.propertyLabels.status"
        })
      }), isCPQQuote(quote) ? /*#__PURE__*/_jsx(CPQQuoteStatusTag, {
        quote: quote
      }) : /*#__PURE__*/_jsx(StatusTag, {
        use: statusConfig.use,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: statusConfig.message,
          options: statusConfig.options
        })
      })]
    })]
  });
};
export default QuoteHighlightDetails;